import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Pagination from '../../components/ui/Pagination';
import { productsCatalogService } from '../../services/catalogService';
import { useToast } from '../../contexts/ToastContext';
import ProductDetailModal from './components/ProductDetailModal';

const CATEGORY_OPTIONS = [
  { value: 'networking', label: 'Networking' },
  { value: 'security', label: 'Security' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'software', label: 'Software' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'iot', label: 'IoT' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'other', label: 'Other' },
];

const LICENSING_OPTIONS = [
  { value: 'capex', label: 'CAPEX' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'saas', label: 'SaaS' },
  { value: 'perpetual', label: 'Perpetual' },
];

const MARGIN_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const EMPTY_FORM = {
  productName: '',
  category: '',
  oemBrand: '',
  modelSku: '',
  licensingModel: '',
  description: '',
  targetIndustry: '',
  priceRange: '',
  marginCategory: 'medium',
  catalogStatus: 'active',
};

const PAGE_SIZE = 10;

const getCategoryColor = (cat) => {
  const map = {
    networking: 'bg-blue-100 text-blue-700',
    security: 'bg-red-100 text-red-700',
    cloud: 'bg-purple-100 text-purple-700',
    software: 'bg-green-100 text-green-700',
    telecom: 'bg-orange-100 text-orange-700',
    iot: 'bg-cyan-100 text-cyan-700',
    hardware: 'bg-slate-100 text-slate-700',
    other: 'bg-gray-100 text-gray-700',
  };
  return map?.[cat] || 'bg-muted text-muted-foreground';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    sunset: 'bg-amber-100 text-amber-700',
    draft: 'bg-slate-100 text-slate-600',
  };
  return map?.[status] || 'bg-muted text-muted-foreground';
};

const getMarginColor = (margin) => {
  const map = {
    high: 'text-emerald-600',
    medium: 'text-amber-600',
    low: 'text-red-500',
  };
  return map?.[margin] || 'text-muted-foreground';
};

export default function ProductsCatalog() {
  const toast = useToast();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeKpi, setActiveKpi] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  // Close category dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryRef?.current && !categoryRef?.current?.contains(e?.target)) {
        setCategoryOpen(false);
        setCategorySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await productsCatalogService?.getAll();
      setProducts(data);
    } catch (err) {
      toast?.error('Failed to load products catalog.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formData?.productName?.trim()) {
      toast?.error('Product name is required.');
      return;
    }
    try {
      if (editingId) {
        const updated = await productsCatalogService?.update(editingId, formData);
        setProducts(prev => prev?.map(p => p?.id === editingId ? updated : p));
        toast?.success('Product updated in catalog.');
      } else {
        const created = await productsCatalogService?.create(formData);
        setProducts(prev => [created, ...prev]);
        toast?.success('Product added to catalog.');
      }
      resetForm();
    } catch (err) {
      toast?.error(editingId ? 'Failed to update product.' : 'Failed to add product.');
      console.error(err);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      productName: product?.productName || '',
      category: product?.category || '',
      oemBrand: product?.oemBrand || '',
      modelSku: product?.modelSku || '',
      licensingModel: product?.licensingModel || '',
      description: product?.description || '',
      targetIndustry: product?.targetIndustry || '',
      priceRange: product?.priceRange || '',
      marginCategory: product?.marginCategory || 'medium',
      catalogStatus: product?.catalogStatus || 'active',
    });
    setEditingId(product?.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      await productsCatalogService?.delete(id);
      setProducts(prev => prev?.filter(p => p?.id !== id));
      toast?.success('Product removed from catalog.');
    } catch (err) {
      toast?.error('Failed to delete product.');
      console.error(err);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleModalUpdate = async (id, data) => {
    try {
      const updated = await productsCatalogService?.update(id, data);
      setProducts(prev => prev?.map(p => p?.id === id ? updated : p));
      setSelectedProduct(updated);
      toast?.success('Product updated.');
    } catch (err) {
      toast?.error('Failed to update product.');
      console.error(err);
    }
  };

  const handleModalDelete = async (id) => {
    try {
      await productsCatalogService?.delete(id);
      setProducts(prev => prev?.filter(p => p?.id !== id));
      toast?.success('Product removed from catalog.');
    } catch (err) {
      toast?.error('Failed to delete product.');
      console.error(err);
    }
  };

  const handleModalArchive = async (id) => {
    try {
      const updated = await productsCatalogService?.update(id, { catalogStatus: 'sunset' });
      setProducts(prev => prev?.map(p => p?.id === id ? updated : p));
      toast?.success('Product archived (set to Sunset).');
    } catch (err) {
      toast?.error('Failed to archive product.');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const filtered = products?.filter(p => {
    const matchSearch = !search || p?.productName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      p?.oemBrand?.toLowerCase()?.includes(search?.toLowerCase()) ||
      p?.modelSku?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !statusFilter || p?.catalogStatus === statusFilter;
    const matchCategory = !categoryFilter || p?.category === categoryFilter;
    return matchSearch && matchStatus && matchCategory;
  });

  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleKpiClick = (filterValue) => {
    if (activeKpi === filterValue) {
      // Deselect — clear filter
      setActiveKpi(null);
      setStatusFilter('');
    } else {
      setActiveKpi(filterValue);
      setStatusFilter(filterValue);
    }
    setPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Products Catalog - PartnerHub</title>
        <meta name="description" content="Master products catalog for partner portfolio mapping" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/20">
        <Header />

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">

            {/* Page Header */}
            <div className="mb-32">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20 mb-24">
                <div className="flex items-center gap-16">
                  <div className="p-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                    <Icon name="Package" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">Products Catalog</h1>
                    <p className="text-slate-500 mt-4">Master catalog — partners select from this list</p>
                  </div>
                </div>
                {/* Add Product button moved to toolbar below KPI cards */}
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-24">
                {[
                  {
                    label: 'Total Products',
                    value: products?.length,
                    icon: 'Package',
                    color: 'text-blue-600',
                    filterValue: '',
                    borderColor: 'border-blue-500',
                    bgActive: 'bg-blue-50',
                    trendLabel: 'All catalog items',
                    iconBg: 'from-blue-500 to-blue-600',
                  },
                  {
                    label: 'Active',
                    value: products?.filter(p => p?.catalogStatus === 'active')?.length,
                    icon: 'CheckCircle2',
                    color: 'text-emerald-600',
                    filterValue: 'active',
                    borderColor: 'border-emerald-500',
                    bgActive: 'bg-emerald-50',
                    trendLabel: 'Live in catalog',
                    iconBg: 'from-emerald-500 to-emerald-600',
                  },
                  {
                    label: 'Sunset',
                    value: products?.filter(p => p?.catalogStatus === 'sunset')?.length,
                    icon: 'Sunset',
                    color: 'text-amber-600',
                    filterValue: 'sunset',
                    borderColor: 'border-amber-500',
                    bgActive: 'bg-amber-50',
                    trendLabel: 'End-of-life products',
                    iconBg: 'from-amber-500 to-amber-600',
                  },
                  {
                    label: 'Draft',
                    value: products?.filter(p => p?.catalogStatus === 'draft')?.length,
                    icon: 'FileEdit',
                    color: 'text-slate-500',
                    filterValue: 'draft',
                    borderColor: 'border-slate-400',
                    bgActive: 'bg-slate-50',
                    trendLabel: 'Pending review',
                    iconBg: 'from-slate-400 to-slate-500',
                  },
                ]?.map(stat => {
                  const isActive = activeKpi === stat?.filterValue;
                  return (
                    <div
                      key={stat?.label}
                      onClick={() => handleKpiClick(stat?.filterValue)}
                      className={`
                        relative bg-white rounded-xl border-2 p-20 shadow-sm cursor-pointer
                        transition-all duration-200 select-none
                        hover:-translate-y-1 hover:shadow-md
                        ${isActive
                          ? `${stat?.borderColor} ${stat?.bgActive} shadow-md -translate-y-1`
                          : 'border-slate-200 hover:border-slate-300'
                        }
                      `}
                    >
                      <div className="flex items-center gap-12 mb-10">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${stat?.iconBg} flex items-center justify-center shadow-sm`}>
                          <Icon name={stat?.icon} size={18} className="text-white" />
                        </div>
                        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{stat?.label}</span>
                      </div>
                      <p className={`text-3xl font-bold ${stat?.color} leading-none mb-6 text-center`}>{stat?.value}</p>
                      <p className="text-xs text-slate-400 text-center">{stat?.trendLabel}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Add / Edit Form */}
            {showForm && (
              <div className="bg-white border border-slate-200 rounded-2xl p-24 shadow-sm mb-32">
                <h3 className="text-lg font-semibold text-slate-900 mb-20">
                  {editingId ? 'Edit Product' : 'Add New Product to Catalog'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                  <Input
                    label="Product Name *"
                    placeholder="e.g., Cisco Catalyst 9300"
                    value={formData?.productName}
                    onChange={e => handleField('productName', e?.target?.value)}
                  />
                  <Select
                    label="Category"
                    options={CATEGORY_OPTIONS}
                    value={formData?.category}
                    onChange={v => handleField('category', v)}
                    placeholder="Select category"
                  />
                  <Input
                    label="OEM / Brand"
                    placeholder="e.g., Cisco, Fortinet"
                    value={formData?.oemBrand}
                    onChange={e => handleField('oemBrand', e?.target?.value)}
                  />
                  <Input
                    label="Model / SKU"
                    placeholder="e.g., C9300-48P-E"
                    value={formData?.modelSku}
                    onChange={e => handleField('modelSku', e?.target?.value)}
                  />
                  <Select
                    label="Licensing Model"
                    options={LICENSING_OPTIONS}
                    value={formData?.licensingModel}
                    onChange={v => handleField('licensingModel', v)}
                    placeholder="Select licensing"
                  />
                  <Input
                    label="Target Industry"
                    placeholder="e.g., Enterprise, SMB"
                    value={formData?.targetIndustry}
                    onChange={e => handleField('targetIndustry', e?.target?.value)}
                  />
                  <Input
                    label="Price Range"
                    placeholder="e.g., OMR 5,000–OMR 15,000"
                    value={formData?.priceRange}
                    onChange={e => handleField('priceRange', e?.target?.value)}
                  />
                  <Select
                    label="Margin Category"
                    options={MARGIN_OPTIONS}
                    value={formData?.marginCategory}
                    onChange={v => handleField('marginCategory', v)}
                  />
                  <Select
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={formData?.catalogStatus}
                    onChange={v => handleField('catalogStatus', v)}
                  />
                  <div className="lg:col-span-3">
                    <label className="block text-sm font-medium text-slate-700 mb-6">Description</label>
                    <textarea
                      className="w-full border border-slate-200 rounded-lg px-12 py-10 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
                      rows={3}
                      placeholder="Brief product description..."
                      value={formData?.description}
                      onChange={e => handleField('description', e?.target?.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-12 mt-20">
                  <Button variant="default" onClick={handleSubmit}>
                    {editingId ? 'Update Product' : 'Add to Catalog'}
                  </Button>
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Unified Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-20">
              {/* Search — ~50% of row */}
              <div className="relative" style={{flex: '0 0 40%', maxWidth: '40%'}}>
                <Icon name="Search" size={16} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="w-full pl-36 pr-12 py-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
                  placeholder="Search products..."
                  value={search}
                  onChange={e => { setSearch(e?.target?.value); setPage(1); }}
                />
              </div>
              {/* Right-side controls grouped together */}
              <div className="flex items-center gap-3 ml-auto">
                {/* All Status */}
                <div className="w-44">
                  <Select
                    options={FILTER_OPTIONS}
                    value={statusFilter}
                    onChange={v => { setStatusFilter(v); setPage(1); }}
                    placeholder="All Status"
                    className="h-10"
                  />
                </div>
                {/* Category — Searchable & Selectable Dropdown */}
                <div className="relative w-56" ref={categoryRef}>
                  <button
                    type="button"
                    onClick={() => { setCategoryOpen(prev => !prev); setCategorySearch(''); }}
                    className="w-full h-10 flex items-center justify-between gap-8 px-14 border border-slate-200 rounded-lg text-sm bg-white hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                  >
                    <span className={categoryFilter ? 'text-slate-800 font-medium' : 'text-slate-400'}>
                      {categoryFilter
                        ? CATEGORY_OPTIONS?.find(o => o?.value === categoryFilter)?.label || 'Category' : 'Category'}
                    </span>
                    <Icon
                      name={categoryOpen ? 'ChevronUp' : 'ChevronDown'}
                      size={14}
                      className="text-slate-400 flex-shrink-0"
                    />
                  </button>

                  {categoryOpen && (
                    <div className="absolute z-50 top-full mt-4 left-0 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                      {/* Search input */}
                      <div className="p-8 border-b border-slate-100">
                        <div className="relative">
                          <Icon name="Search" size={13} className="absolute left-8 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input
                            autoFocus
                            type="text"
                            className="w-full pl-28 pr-8 py-7 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-slate-50"
                            placeholder="Search categories..."
                            value={categorySearch}
                            onChange={e => setCategorySearch(e?.target?.value)}
                            onClick={e => e?.stopPropagation()}
                          />
                        </div>
                      </div>

                      {/* Options list */}
                      <ul className="max-h-52 overflow-y-auto py-4">
                        {/* All Categories option */}
                        {('all categories'?.includes(categorySearch?.toLowerCase()) || categorySearch === '') && (
                          <li>
                            <button
                              type="button"
                              onClick={() => { setCategoryFilter(''); setPage(1); setCategoryOpen(false); setCategorySearch(''); }}
                              className={`w-full text-left px-14 py-9 text-sm transition-colors hover:bg-primary/5 ${
                                categoryFilter === '' ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-700'
                              }`}
                            >
                              All Categories
                            </button>
                          </li>
                        )}
                        {CATEGORY_OPTIONS?.filter(opt =>
                          opt?.label?.toLowerCase()?.includes(categorySearch?.toLowerCase())
                        )?.map(opt => (
                          <li key={opt?.value}>
                            <button
                              type="button"
                              onClick={() => { setCategoryFilter(opt?.value); setPage(1); setCategoryOpen(false); setCategorySearch(''); }}
                              className={`w-full text-left px-14 py-9 text-sm transition-colors hover:bg-primary/5 flex items-center justify-between ${
                                categoryFilter === opt?.value ? 'bg-primary/10 text-primary font-semibold' : 'text-slate-700'
                              }`}
                            >
                              <span>{opt?.label}</span>
                              {categoryFilter === opt?.value && (
                                <Icon name="Check" size={13} className="text-primary" />
                              )}
                            </button>
                          </li>
                        ))}
                        {CATEGORY_OPTIONS?.filter(opt =>
                          opt?.label?.toLowerCase()?.includes(categorySearch?.toLowerCase())
                        )?.length === 0 && categorySearch !== '' && (
                          <li className="px-14 py-10 text-sm text-slate-400 text-center">No categories found</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                {/* Add Product */}
                <div className="flex-shrink-0">
                  <Button
                    variant={showForm ? 'outline' : 'default'}
                    iconName={showForm ? 'X' : 'Plus'}
                    iconPosition="left"
                    iconSize={16}
                    onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
                    className="h-10 px-16 whitespace-nowrap"
                  >
                    {showForm ? 'Cancel' : 'Add Product'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              {loading ? (
                <div className="flex items-center justify-center py-64">
                  <div className="flex items-center gap-12 text-slate-500">
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    <span>Loading catalog...</span>
                  </div>
                </div>
              ) : paginated?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-64 text-slate-400">
                  <Icon name="Package" size={40} className="mb-12 opacity-30" />
                  <p className="font-medium">No products found</p>
                  <p className="text-sm mt-4">Add the first product to the master catalog</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Product Name</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Category</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">OEM / Brand</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Model / SKU</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Licensing</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Margin</th>
                        <th className="px-16 py-12 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                        <th className="px-16 py-12 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginated?.map(product => (
                        <tr key={product?.id} className="hover:bg-slate-50/60 transition-colors cursor-pointer" onClick={() => setSelectedProduct(product)}>
                          <td className="px-16 py-14">
                            <div>
                              <p className="text-sm font-semibold text-slate-800 hover:text-primary transition-colors">{product?.productName}</p>
                              {product?.description && (
                                <p className="text-xs text-slate-400 mt-2 line-clamp-1">{product?.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-16 py-14">
                            <span className={`inline-flex px-8 py-4 rounded-md text-xs font-medium ${getCategoryColor(product?.category)}`}>
                              {CATEGORY_OPTIONS?.find(c => c?.value === product?.category)?.label || product?.category}
                            </span>
                          </td>
                          <td className="px-16 py-14 text-sm text-slate-600">{product?.oemBrand || '—'}</td>
                          <td className="px-16 py-14 text-sm text-slate-500 font-mono">{product?.modelSku || '—'}</td>
                          <td className="px-16 py-14 text-sm text-slate-600">
                            {LICENSING_OPTIONS?.find(l => l?.value === product?.licensingModel)?.label || product?.licensingModel || '—'}
                          </td>
                          <td className="px-16 py-14">
                            <span className={`text-sm font-semibold capitalize ${getMarginColor(product?.marginCategory)}`}>
                              {product?.marginCategory || '—'}
                            </span>
                          </td>
                          <td className="px-16 py-14">
                            <span className={`inline-flex px-8 py-4 rounded-full text-xs font-medium capitalize ${getStatusColor(product?.catalogStatus)}`}>
                              {product?.catalogStatus}
                            </span>
                          </td>
                          <td className="px-16 py-14">
                            <div className="flex items-center justify-end gap-4" onClick={e => e?.stopPropagation()}>
                              <button
                                onClick={() => handleEdit(product)}
                                className="p-8 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                title="Edit"
                              >
                                <Icon name="Edit" size={15} />
                              </button>
                              <button
                                onClick={() => navigate(`/products-catalog/${product?.id}`)}
                                className="p-8 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                                title="View Profile"
                              >
                                <Icon name="ExternalLink" size={15} />
                              </button>
                              {deleteConfirm === product?.id ? (
                                <div className="flex items-center gap-4">
                                  <button
                                    onClick={() => handleDelete(product?.id)}
                                    className="px-8 py-4 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="px-8 py-4 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirm(product?.id)}
                                  className="p-8 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="Delete"
                                >
                                  <Icon name="Trash2" size={15} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filtered?.length > PAGE_SIZE && (
              <div className="mt-20">
                <Pagination
                  currentPage={page}
                  totalItems={filtered?.length}
                  itemsPerPage={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onUpdate={handleModalUpdate}
          onDelete={handleModalDelete}
          onArchive={handleModalArchive}
        />
      )}
    </>
  );
}
